import { useState, useEffect, useRef } from 'react'
import technologyScopingData from '../../../shared/data/technologyScoping.json'

/**
 * Technology Scoping Step Component
 * Collects information about the organization's technology platforms
 * to inform threat profile selection and context-weighted scoring
 */
function TechnologyScopingStep({ responses, onSave }) {
  const [localResponses, setLocalResponses] = useState({})
  const isInitializedRef = useRef(false)

  // Initialize from saved responses (only on first mount)
  useEffect(() => {
    // Only run once during initial mount to restore saved responses
    if (!isInitializedRef.current && responses.technologyScoping) {
      setLocalResponses(responses.technologyScoping)
      isInitializedRef.current = true
    } else if (!isInitializedRef.current && !responses.technologyScoping) {
      // Mark as initialized even if no saved responses
      isInitializedRef.current = true
    }
  }, [responses.technologyScoping])

  const handleResponseChange = (questionId, selectedLabel, weight, otherText) => {
    const newResponses = {
      ...localResponses,
      [questionId]: {
        value: selectedLabel,
        weight: weight,
        ...(otherText !== undefined ? { otherText } : {})
      }
    }
    setLocalResponses(newResponses)
    onSave('technologyScoping', newResponses)
  }

  const getResponseValue = (questionId) => {
    const response = localResponses[questionId]
    return response?.value || ''
  }

  const getOtherText = (questionId) => {
    return localResponses[questionId]?.otherText || ''
  }

  const getCompletionStats = () => {
    const totalQuestions = technologyScopingData.sections.reduce(
      (sum, section) => sum + section.questions.length,
      0
    )
    const answeredQuestions = Object.entries(localResponses).filter(([key, resp]) => {
      if (!key.startsWith('ts-')) return false
      // "Other" without specifying doesn't count
      if (resp?.value === 'Other' && !resp?.otherText?.trim()) return false
      return !!resp?.value
    }).length
    return { answered: answeredQuestions, total: totalQuestions }
  }

  const stats = getCompletionStats()

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="card p-6 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Technology Environment</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Understanding your technology platforms helps us tailor security recommendations and threat analysis to your specific environment.
            </p>
            <div className="mt-3 flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {stats.answered} of {stats.total} answered
              </div>
              {stats.answered === stats.total && (
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  Section complete
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Optional skip notice */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Not sure about your technology stack?
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
              You can select "Unknown" for any question. This section is optional, but providing accurate information helps generate more relevant recommendations.
            </p>
          </div>
        </div>
      </div>

      {/* Questions by Section */}
      {technologyScopingData.sections.map((section) => (
        <div key={section.id} className="card">
          <div className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700/30">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {section.name}
            </h3>
            {section.description && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {section.description}
              </p>
            )}
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {section.questions.map((question, qIdx) => {
              const currentValue = getResponseValue(question.id)
              const isOtherIncomplete = currentValue === 'Other' && !getOtherText(question.id)?.trim()
              const isAnswered = currentValue !== '' && !isOtherIncomplete

              return (
                <div key={question.id} className="p-6">
                  <div className="flex items-start space-x-4">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      isAnswered
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      {isAnswered ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        qIdx + 1
                      )}
                    </span>
                    <div className="flex-1">
                      <label className="block text-gray-900 dark:text-white font-medium mb-3">
                        {question.question}
                      </label>

                      <select
                        value={currentValue}
                        onChange={(e) => {
                          const selectedOption = question.options.find(opt => opt.label === e.target.value)
                          if (selectedOption) {
                            handleResponseChange(question.id, selectedOption.label, selectedOption.weight)
                          } else {
                            // Clear selection
                            const newResponses = { ...localResponses }
                            delete newResponses[question.id]
                            setLocalResponses(newResponses)
                            onSave('technologyScoping', newResponses)
                          }
                        }}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Select an option...</option>
                        {question.options.map((option, idx) => (
                          <option key={idx} value={option.label}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      {/* Free text input when "Other" is selected */}
                      {currentValue === 'Other' && question.options?.find(o => o.label === 'Other')?.allowFreeText && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Please specify <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={getOtherText(question.id)}
                            onChange={(e) => {
                              const opt = question.options.find(o => o.label === 'Other')
                              handleResponseChange(question.id, 'Other', opt?.weight || 1.0, e.target.value)
                            }}
                            placeholder="Enter the platform or tool name..."
                            className={`w-full p-2.5 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                              !getOtherText(question.id)?.trim()
                                ? 'border-red-300 dark:border-red-700'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}
                          />
                          {!getOtherText(question.id)?.trim() && (
                            <p className="text-xs text-red-500 dark:text-red-400 mt-1">Required when "Other" is selected</p>
                          )}
                        </div>
                      )}

                      {/* Show platform hint as a subtle indicator */}
                      {currentValue && currentValue !== 'Other' && !question.followUps && (
                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          This selection will be used to tailor threat scenarios and recommendations.
                        </div>
                      )}

                      {/* Conditional follow-up questions */}
                      {question.followUps && currentValue && question.followUps
                        .filter(fu => fu.showWhen.includes(currentValue))
                        .map(fu => (
                          <div key={fu.id} className="mt-4 ml-4 pl-4 border-l-2 border-teal-300 dark:border-teal-700">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">
                              {fu.question}
                            </label>
                            <select
                              value={getResponseValue(fu.id)}
                              onChange={(e) => {
                                const selectedOption = fu.options.find(opt => opt.label === e.target.value)
                                if (selectedOption) {
                                  handleResponseChange(fu.id, selectedOption.label, selectedOption.weight)
                                } else {
                                  const newResponses = { ...localResponses }
                                  delete newResponses[fu.id]
                                  setLocalResponses(newResponses)
                                  onSave('technologyScoping', newResponses)
                                }
                              }}
                              className="w-full p-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            >
                              <option value="">Select...</option>
                              {fu.options.map((opt, idx) => (
                                <option key={idx} value={opt.label}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default TechnologyScopingStep
